import React, { useState, useEffect, useRef, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Smartphone, QrCode, Wifi, WifiOff, RefreshCw, Power, Loader2, AlertTriangle, Trash2 } from "lucide-react";

const statusConfig = {
  not_connected: { label: "Not Connected", color: "bg-slate-200 text-slate-600", icon: WifiOff },
  initializing: { label: "Initializing", color: "bg-yellow-100 text-yellow-700", icon: Loader2 },
  generating_qr: { label: "Generating QR", color: "bg-yellow-100 text-yellow-700", icon: Loader2 },
  qr_ready: { label: "QR Ready - Scan Now", color: "bg-blue-100 text-blue-700", icon: QrCode },
  authenticated: { label: "Authenticated", color: "bg-[#22c55e]/20 text-[#16a34a]", icon: Wifi },
  connected: { label: "Connected", color: "bg-[#22c55e] text-white", icon: Wifi },
  disconnected: { label: "Disconnected", color: "bg-red-100 text-red-600", icon: WifiOff },
  auth_failure: { label: "Auth Failed", color: "bg-red-100 text-red-600", icon: WifiOff },
  error: { label: "Error", color: "bg-red-100 text-red-600", icon: WifiOff },
};

export default function ConnectWhatsApp() {
  const [status, setStatus] = useState("not_connected");
  const [qr, setQr] = useState(null);
  const [logs, setLogs] = useState([]);
  const [phone, setPhone] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [wipeOnDisconnect, setWipeOnDisconnect] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [wipeOnReset, setWipeOnReset] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);

  const fetchDiagnostics = async () => {
    try {
      const { data } = await authAxios.get(`${API}/whatsapp/diagnostics`);
      setDiagnostics(data);
    } catch (err) {
      setDiagnostics({ error: err.response?.data?.detail || err.message || "Failed to fetch" });
    }
  };
  const pollRef = useRef(null);
  const logEndRef = useRef(null);
  const lastQrRef = useRef(null);
  const qrStaleCountRef = useRef(0);

  const pollStatus = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API}/whatsapp/status`);
      const newStatus = data.status || "not_connected";
      const newQr = data.qr || null;

      setStatus(newStatus);
      setLogs(data.logs || []);
      setPhone(data.phone_number || "");

      // QR freshness tracking
      if (newStatus === "qr_ready" && newQr) {
        if (newQr === lastQrRef.current) {
          qrStaleCountRef.current += 1;
        } else {
          qrStaleCountRef.current = 0;
          lastQrRef.current = newQr;
        }
        setQr(newQr);
      } else if (newStatus === "connected" || newStatus === "authenticated") {
        setQr(null);
        qrStaleCountRef.current = 0;
        lastQrRef.current = null;
      } else if (newStatus === "generating_qr" || newStatus === "initializing") {
        // Keep showing old QR while new one generates, or show loading
        if (!newQr) setQr(null);
        qrStaleCountRef.current = 0;
      } else {
        setQr(newQr);
        qrStaleCountRef.current = 0;
        lastQrRef.current = null;
      }

      // Auto-clear connecting/disconnecting flags
      if (["connected", "disconnected", "not_connected", "error", "auth_failure"].includes(newStatus)) {
        setConnecting(false);
        setDisconnecting(false);
      }
      if (["qr_ready", "generating_qr", "initializing"].includes(newStatus)) {
        setDisconnecting(false);
      }
    } catch (err) {
      console.error("Poll error:", err);
    }
  }, []);

  // Adaptive polling: faster during QR/init states, slower when idle/connected
  useEffect(() => {
    pollStatus();
    const getInterval = () => {
      if (["qr_ready", "generating_qr", "initializing", "authenticated"].includes(status)) return 2000;
      if (status === "connected") return 10000;
      return 5000;
    };
    pollRef.current = setInterval(pollStatus, getInterval());
    return () => clearInterval(pollRef.current);
  }, [pollStatus, status]);

  useEffect(() => {
    if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const handleConnect = async () => {
    setConnecting(true);
    setActionError("");
    try {
      const { data } = await authAxios.post(`${API}/whatsapp/connect`, {});
      if (data.status === "already_connected") {
        setStatus("connected");
        setConnecting(false);
        return;
      }
      await pollStatus();
    } catch (err) {
      console.error("Connect error:", err);
      setActionError(err.response?.data?.detail || "Failed to start connection. Try again.");
      setConnecting(false);
      // Auto-fetch diagnostics on connect failure to surface root cause
      fetchDiagnostics();
    }
  };

  const handleDisconnect = async (wipe = false) => {
    setDisconnecting(true);
    setActionError("");
    try {
      const { data } = await authAxios.post(`${API}/whatsapp/disconnect`, { wipe });
      setQr(null);
      setStatus("disconnected");
      setLogs([]);
      setPhone("");
      lastQrRef.current = null;
      qrStaleCountRef.current = 0;
      if (wipe && data?.wiped) {
        const w = data.wiped;
        const total = (w.conversations || 0) + (w.messages || 0) + (w.group_selections || 0);
        setActionError(null);
        // Friendly success notice rendered as info; reuse the warning slot briefly
        setLogs([{ timestamp: new Date().toISOString(), message: `🧹 Wiped: ${w.conversations} conversations, ${w.messages} messages, ${w.group_selections} groups (${total} records). Ready for a new number.` }]);
      }
      setTimeout(pollStatus, 1500);
    } catch (err) {
      console.error("Disconnect error:", err);
      setActionError(err.response?.data?.detail || "Disconnect failed. Try again.");
      setQr(null);
      setStatus("disconnected");
    } finally {
      setDisconnecting(false);
      setConnecting(false);
    }
  };

  const handleReconnect = async () => {
    setConnecting(true);
    setActionError("");
    setQr(null);
    lastQrRef.current = null;
    qrStaleCountRef.current = 0;
    try {
      await authAxios.post(`${API}/whatsapp/reconnect`, {});
      // Poll aggressively to get fresh QR
      for (let i = 0; i < 3; i++) {
        await new Promise(r => setTimeout(r, 2000));
        await pollStatus();
      }
    } catch (err) {
      console.error("Reconnect error:", err);
      setActionError(err.response?.data?.detail || "Reconnect failed. Try again.");
      setConnecting(false);
    }
  };

  const handleReset = async (wipe = false) => {
    setResetting(true);
    setActionError("");
    setQr(null);
    setLogs([]);
    setPhone("");
    lastQrRef.current = null;
    qrStaleCountRef.current = 0;
    try {
      await authAxios.post(`${API}/whatsapp/reset?wipe_data=${wipe ? "true" : "false"}`);
      setStatus("initializing");
      // Aggressively poll to surface the new QR ASAP
      for (let i = 0; i < 8; i++) {
        await new Promise(r => setTimeout(r, 2000));
        await pollStatus();
      }
    } catch (err) {
      console.error("Reset error:", err);
      setActionError(err.response?.data?.detail || "Reset failed. Try again.");
    } finally {
      setResetting(false);
      setResetDialogOpen(false);
      setWipeOnReset(false);
    }
  };

  const cfg = statusConfig[status] || statusConfig.not_connected;
  const StatusIcon = cfg.icon;
  const isIdle = ["not_connected", "disconnected", "error", "auth_failure"].includes(status);
  const isActive = ["qr_ready", "generating_qr", "initializing", "authenticated"].includes(status);
  const isConnected = status === "connected";

  return (
    <div className="p-8 max-w-4xl" data-testid="connect-whatsapp-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#0f172a]">Connect WhatsApp</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Link your WhatsApp account by scanning the QR code
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connection Card */}
        <Card data-testid="connection-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                Connection Status
              </CardTitle>
              <Badge className={cfg.color} data-testid="connection-status-badge">
                <StatusIcon className={`w-3 h-3 mr-1 ${(status === "initializing" || status === "generating_qr") ? "animate-spin" : ""}`} />
                {cfg.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Error banner */}
            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="action-error">
                <div>{actionError}</div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 text-xs text-red-600 hover:bg-red-100"
                  onClick={fetchDiagnostics}
                  data-testid="show-diagnostics-btn"
                >
                  Show service diagnostics
                </Button>
              </div>
            )}

            {/* Diagnostic panel — appears when sidecar isn't responding */}
            {diagnostics && (
              <div className="p-3 bg-slate-50 border border-slate-300 rounded-lg text-xs font-mono" data-testid="diagnostics-panel">
                <div className="flex items-center justify-between mb-2">
                  <b className="text-slate-700">WhatsApp Service Diagnostics</b>
                  <button onClick={() => setDiagnostics(null)} className="text-slate-500 hover:text-slate-700">×</button>
                </div>
                <pre className="whitespace-pre-wrap break-all max-h-64 overflow-auto text-[11px] leading-relaxed">{JSON.stringify(diagnostics, null, 2)}</pre>
                <p className="mt-2 text-[11px] text-slate-600 font-sans">
                  If <b>status</b> is not <code>running</code> or <code>external</code>, copy this output and share it with support.
                </p>
              </div>
            )}

            {/* QR Code Display */}
            {status === "qr_ready" && qr ? (
              <div className="flex flex-col items-center" data-testid="qr-display">
                <div className="bg-white p-4 rounded-xl border-2 border-dashed border-blue-300">
                  <img src={qr} alt="WhatsApp QR Code" className="w-56 h-56" data-testid="qr-code-image" />
                </div>
                <p className="text-sm text-muted-foreground mt-3 text-center">
                  Open WhatsApp &gt; Settings &gt; Linked Devices &gt; Link a Device
                </p>
                <p className="text-xs text-blue-500 mt-1">QR refreshes automatically</p>
              </div>
            ) : isConnected ? (
              <div className="text-center py-6" data-testid="connected-info">
                <div className="w-16 h-16 rounded-full bg-[#22c55e]/10 flex items-center justify-center mx-auto mb-3">
                  <Wifi className="w-8 h-8 text-[#22c55e]" />
                </div>
                <p className="text-base font-medium text-[#0f172a]">WhatsApp Connected</p>
                {phone && <p className="text-sm text-muted-foreground mt-1">Phone: +{phone}</p>}
              </div>
            ) : (status === "initializing" || status === "generating_qr") ? (
              <div className="text-center py-6" data-testid="generating-status">
                <Loader2 className="w-10 h-10 animate-spin text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  {status === "initializing" ? "Initializing WhatsApp client..." : "Generating QR code..."}
                </p>
                <p className="text-xs text-muted-foreground mt-1">This may take 10-20 seconds</p>
              </div>
            ) : (
              <div className="text-center py-6" data-testid="not-connected-info">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <QrCode className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Click the button below to start connecting
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 flex-wrap">
              {isIdle ? (
                <Button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="flex-1 bg-[#22c55e] hover:bg-[#16a34a] text-white"
                  data-testid="connect-button"
                >
                  {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Power className="w-4 h-4 mr-2" />}
                  {connecting ? "Connecting..." : "Start Connection"}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleReconnect}
                    disabled={connecting || disconnecting}
                    variant="outline"
                    className="flex-1"
                    data-testid="reconnect-button"
                  >
                    {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {connecting ? "Restarting..." : isConnected ? "Reconnect" : "Restart Connection"}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        disabled={disconnecting || connecting}
                        variant="destructive"
                        className="flex-1"
                        data-testid="disconnect-button"
                      >
                        {disconnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <WifiOff className="w-4 h-4 mr-2" />}
                        {disconnecting ? "Disconnecting..." : "Disconnect"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="disconnect-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-500" /> Disconnect WhatsApp?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-3">
                            <p>This will sign out your WhatsApp session{phone ? ` (+${phone})` : ""}.</p>
                            <div className="flex items-start gap-2 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900">
                              <input
                                type="checkbox"
                                id="wipe-all-data"
                                checked={wipeOnDisconnect}
                                onChange={(e) => setWipeOnDisconnect(e.target.checked)}
                                className="mt-1"
                                data-testid="wipe-all-data-checkbox"
                              />
                              <label htmlFor="wipe-all-data" className="text-sm">
                                <b>Also wipe all data</b> (conversations, messages, group selections, flow states).
                                <br/>
                                <span className="text-xs opacity-80">Recommended if you'll connect a different WhatsApp number — frees space and starts clean. Bot settings, flows, commands and media library are kept.</span>
                              </label>
                            </div>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="disconnect-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => { handleDisconnect(wipeOnDisconnect); setWipeOnDisconnect(false); }}
                          className="bg-red-500 hover:bg-red-600 text-white"
                          data-testid="disconnect-confirm"
                        >
                          {wipeOnDisconnect ? "Disconnect & wipe data" : "Yes, disconnect"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>

            {/* Reset / Start fresh — always available */}
            <div className="mt-3 pt-3 border-t border-slate-200">
              <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    disabled={resetting || connecting || disconnecting}
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200"
                    data-testid="reset-instance-button"
                  >
                    {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    {resetting ? "Reseteando..." : "Reset / Empezar desde cero"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="reset-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" /> ¿Resetear la instancia?
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3">
                        <p>Esto <b>cierra</b> cualquier sesión activa, <b>borra</b> los archivos de autenticación de WhatsApp y <b>genera una nueva QR</b>. Úsalo cuando la QR esté congelada o quieras conectar un número diferente.</p>
                        <div className="flex items-start gap-2 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900">
                          <input
                            type="checkbox"
                            id="wipe-on-reset"
                            checked={wipeOnReset}
                            onChange={(e) => setWipeOnReset(e.target.checked)}
                            className="mt-1"
                            data-testid="wipe-on-reset-checkbox"
                          />
                          <label htmlFor="wipe-on-reset" className="text-sm">
                            <b>Borrar también todas las conversaciones y mensajes</b>
                            <br/>
                            <span className="text-xs opacity-80">Recomendado si vas a conectar un número distinto. Tus flujos, comandos, configuración del bot y media library se conservan.</span>
                          </label>
                        </div>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="reset-cancel">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => { e.preventDefault(); handleReset(wipeOnReset); }}
                      className="bg-red-500 hover:bg-red-600 text-white"
                      data-testid="reset-confirm"
                    >
                      {wipeOnReset ? "Resetear y borrar datos" : "Sí, resetear"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>

        {/* Debug Log Panel */}
        <Card data-testid="debug-log-panel">
          <CardHeader>
            <CardTitle className="text-base">Connection Log</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[340px]">
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No logs yet. Start a connection to see activity.
                </p>
              ) : (
                <div className="space-y-2 pr-3">
                  {logs.map((log, i) => (
                    <div key={i} className="flex gap-2 text-xs" data-testid={`log-entry-${i}`}>
                      <span className="text-muted-foreground whitespace-nowrap shrink-0">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`${
                        log.message.includes("Error") || log.message.includes("failure")
                          ? "text-red-600"
                          : log.message.includes("connected") || log.message.includes("Authenticated")
                          ? "text-[#22c55e]"
                          : log.message.includes("QR")
                          ? "text-blue-600"
                          : "text-[#0f172a]"
                      }`}>
                        {log.message}
                      </span>
                    </div>
                  ))}
                  <div ref={logEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
